
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Eye, CheckCircle, X } from 'lucide-react';
import { Order } from '@/components/profile/order-types';
import OrderStatusBadge from '@/components/profile/OrderStatusBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from 'react-router-dom';

const OrdersManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchOrders, setSearchOrders] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isUpdateStatusDialogOpen, setIsUpdateStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');

  // Fetch all orders
  const { data: orders = [], isLoading, refetch } = useQuery<Order[]>({
    queryKey: ['adminOrders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          status,
          amount,
          currency,
          created_at,
          delivery_address,
          shipping_method,
          user_id,
          seller_id,
          listings(id, title, images, stock)
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }
      
      // Fetch buyer profiles
      if (data && data.length > 0) {
        const buyerIds = [...new Set(data.map(order => order.user_id))];
        const sellerIds = [...new Set(data.map(order => order.seller_id))];
        const allUserIds = [...new Set([...buyerIds, ...sellerIds])];
        
        const { data: userProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, phone_number, email')
          .in('id', allUserIds);
          
        if (profilesError) {
          console.error('Error fetching user profiles:', profilesError);
          throw profilesError;
        }
        
        // Attach profiles to orders
        return data.map(order => ({
          ...order,
          buyer_profile: userProfiles?.find(profile => profile.id === order.user_id) || null,
          seller_profile: userProfiles?.find(profile => profile.id === order.seller_id) || null
        }));
      }
      
      return data as unknown as Order[];
    },
  });

  // Filter orders based on search
  const filteredOrders = searchOrders 
    ? orders.filter(order => 
        order.id.toLowerCase().includes(searchOrders.toLowerCase()) || 
        order.listings?.title.toLowerCase().includes(searchOrders.toLowerCase()) ||
        order.buyer_profile?.full_name?.toLowerCase().includes(searchOrders.toLowerCase()) ||
        order.buyer_profile?.email?.toLowerCase().includes(searchOrders.toLowerCase())
      )
    : orders;

  // Handle updating order status
  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;
    
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedOrder.id);
      
      if (error) throw error;
      
      toast({
        title: "Order Updated",
        description: `Order status has been updated to "${newStatus}"`,
      });
      
      refetch();
    } catch (error: any) {
      console.error('Error updating order:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred while updating the order",
        variant: "destructive",
      });
    } finally {
      setIsUpdateStatusDialogOpen(false);
      setSelectedOrder(null);
      setNewStatus('');
    }
  };

  // Navigate to product detail page
  const handleViewProduct = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  // Open status update dialog
  const openStatusDialog = (order: Order, status: string) => {
    setSelectedOrder(order);
    setNewStatus(status);
    setIsUpdateStatusDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Order Management</CardTitle>
          <CardDescription>
            View and manage all orders placed on the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Search orders..."
                className="pl-8"
                value={searchOrders}
                onChange={(e) => setSearchOrders(e.target.value)}
              />
            </div>
          </div>
          
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Order ID</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">Loading orders...</TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">No orders found</TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">
                        {order.id.substring(0, 8)}...
                      </TableCell>
                      <TableCell>
                        {order.listings?.title || "Unknown product"}
                      </TableCell>
                      <TableCell>
                        {order.buyer_profile?.full_name || "Unknown buyer"}
                        <div className="text-xs text-gray-500">{order.buyer_profile?.email}</div>
                      </TableCell>
                      <TableCell>
                        {order.seller_profile?.full_name || "Unknown seller"}
                      </TableCell>
                      <TableCell>
                        {order.currency} {Number(order.amount).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge status={order.status} />
                      </TableCell>
                      <TableCell>
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-1">
                          {order.listings?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewProduct(order.listings!.id)}
                              title="View product"
                            >
                              <Eye size={16} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-green-600"
                            onClick={() => openStatusDialog(order, 'completed')}
                            title="Mark as completed"
                          >
                            <CheckCircle size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600"
                            onClick={() => openStatusDialog(order, 'cancelled')}
                            title="Mark as cancelled"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <AlertDialog open={isUpdateStatusDialogOpen} onOpenChange={setIsUpdateStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Order Status</AlertDialogTitle>
            <AlertDialogDescription>
              {newStatus === 'completed' 
                ? "Are you sure you want to mark this order as completed?"
                : "Are you sure you want to cancel this order?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUpdateStatus}
              className={newStatus === 'completed' ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white"}
            >
              {newStatus === 'completed' ? "Complete Order" : "Cancel Order"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrdersManagement;
